import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useToast } from "@/components/ui/use-toast";
import { RoleBadge } from "@/components/users/role-badge";
import {
  DemoAccount,
  useDemoAccounts,
  useProvisionDemoAccounts,
} from "@/lib/api/demo-accounts";
import { Loader2, RotateCcw } from "lucide-react";
import { useCallback, useState } from "react";

/**
 * Provisioning controls for the demo accounts.
 * Lives in Settings -> Dev
 * Provision converges the roster to its declared state 
 * Reset additionally wipes accumulated test activity
 */

type PendingAction = { scope: "all" | "one"; key?: string } | null;

const errorToast = (title: string, description: string) => ({
  title,
  description,
  className:
    "bottom-0 left-0 flex fixed md:max-w-[420px] md:bottom-4 md:left-4 border-2 border-red-500 bg-red-50 text-red-900",
});

export function DemoAccountsSettings() {
  const { toast } = useToast();
  const accountsQuery = useDemoAccounts();
  const provision = useProvisionDemoAccounts();

  const [confirming, setConfirming] = useState<PendingAction>(null);

  const run = useCallback(
    (
      input: { keys?: string[]; fresh?: boolean },
      successMessage: string,
      onDone?: () => void,
    ) => {
      provision.mutate(input, {
        onSuccess: () => {
          toast({ title: "Done", description: successMessage });
          onDone?.();
        },
        onError: (error) => {
          toast(
            errorToast(
              "Provisioning failed",
              error instanceof Error ? error.message : "Unknown error",
            ),
          );
          onDone?.();
        },
      });
    },
    [provision, toast],
  );

  const onConfirm = useCallback(() => {
    if (!confirming) return;
    const target = confirming;

    run(
      {
        fresh: true,
        ...(target.scope === "one" ? { keys: [target.key as string] } : {}),
      },
      target.scope === "one"
        ? `${target.key} was reset to its seeded state.`
        : "All demo accounts were reset to their seeded state.",
      () => setConfirming(null),
    );
  }, [confirming, run]);

  const busy = provision.isPending;

  const isResetting = (account: DemoAccount) =>
    busy &&
    provision.variables?.fresh === true &&
    provision.variables?.keys?.[0] === account.key;

  return (
    <div className="space-y-8 py-6">
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Demo accounts</h2>
          <p className="text-sm text-stone-600">
            Demo accounts with real role relationships, used from the{" "}
            <a href="/demo-accounts" className="underline">
              demo login picker
            </a>
            . <strong>Provision</strong> creates anything missing and repairs
            roles and relationships; never deletes. <strong>Reset</strong>{" "}
            also wipes accumulated test activity (mentorships, messages, notes,
            Sendbird channel history and deactivation records) before
            re-provisioning.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => run({}, "Demo accounts provisioned.", undefined)}
          >
            {busy && provision.variables?.fresh !== true && (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            )}
            Provision (non-destructive)
          </Button>

          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() => setConfirming({ scope: "all" })}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset all
          </Button>
        </div>

        {accountsQuery.isError && (
          <p className="text-sm text-red-700">
            Could not load demo accounts. This environment may have them
            disabled.
          </p>
        )}

        {accountsQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading accounts…
          </div>
        )}

        {accountsQuery.data && (
          <div className="divide-y rounded-md border">
            {accountsQuery.data.map((account) => (
              <div
                key={account.key}
                className="flex flex-wrap items-center justify-between gap-3 p-3"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {account.displayName}
                    </span>
                    {account.systemRoles.map((role) => (
                      <RoleBadge key={role} role={role} size="sm" />
                    ))}
                    {!account.seeded && (
                      <span className="text-xs text-amber-700">not seeded</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {account.phone}
                    {account.hasOnboarded === false && " · not onboarded"}
                  </p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || !account.seeded}
                  onClick={() =>
                    setConfirming({ scope: "one", key: account.key })
                  }
                >
                  {isResetting(account) ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-1 h-3 w-3" />
                  )}
                  Reset
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmActionDialog
        isOpen={!!confirming}
        onClose={() => setConfirming(null)}
        onConfirm={onConfirm}
        loading={busy}
        title={
          confirming?.scope === "all"
            ? "Reset all demo accounts"
            : "Reset demo account"
        }
        itemName={confirming?.key}
        description="Deletes mentorships, messages, notes, profile and Sendbird channel history for the selected account(s), then re-provisions them. Training-simulator progress, already-queued jobs and analytics are not restored."
        confirmText="Reset"
      />
    </div>
  );
}
